import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Building2,
  FileText,
  ClipboardList,
  Bell,
  MessageSquare,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Users,
  Cog,
  DollarSign,
  ClipboardCheck,
  Globe,
  CreditCard,
  Mail,
  X,
  Target,
  Moon,
  Sun,
  User,
  LogOut,
  FolderArchive,
  Upload,
  Activity,
  LayoutGrid,
  LifeBuoy,
  TrendingUp,
  CalendarRange,
  Map,
  Lightbulb,
  ListChecks,
} from 'lucide-react';
import { getApiErrorMessage } from '@/api/client';
import { cn } from '@/lib/cn';
import { sectionActiveColorForPath } from '@/lib/section-tokens';
import { isReleaseFeatureEnabled } from '@/lib/release-flags';
import { openZendeskHelpCenterSso } from '@/features/support/lib/zendesk-sso';
import { useUIStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { isSSIRole } from '@/lib/permissions';
import { useLogout } from '@/hooks/useAuth';
import {
  useDashboardNavHidden,
  useDrawingVaultNavHidden,
} from '@/features/dashboard/hooks/useDashboardAccessContext';
import {
  prefetchOrderListData,
  prefetchOrderListPageChunk,
} from '@/features/orders/lib/prefetch-order-list';
import {
  isReportReviewSearchParams,
  isReportReviewSessionActive,
  reportReviewBuildingIdFromSearch,
  reportReviewBuildingIdFromSession,
  reportReviewScopedPath,
} from '@/features/properties/lib/report-review-session';
import {
  SIDEBAR_NAV_ITEMS,
  isAdminChildNavActive,
  isSidebarNavItemVisible,
  type SidebarNavItem,
} from './sidebar-nav';
import { SidebarRecentProperties } from './SidebarRecentProperties';

type IconComponent = React.ComponentType<{ className?: string }>;

type NavChild = {
  label: string;
  path: string;
  icon: IconComponent;
  flag?: SidebarNavItem['flag'];
};

type NavItem = SidebarNavItem & {
  icon: IconComponent;
  children?: NavChild[];
};

/** Icons stay in the component so sidebar-nav.ts stays lucide-free for unit tests. */
const TOP_LEVEL_ICONS: Record<string, IconComponent> = {
  '/dashboard': LayoutDashboard,
  '/opportunities': Target,
  '/properties': Building2,
  '/reports': FileText,
  '/teams': Users,
  '/reports?tab=value-opportunity': TrendingUp,
  '/orders': ClipboardList,
  '/drawing-vault': FolderArchive,
  '/drawings/reports': FolderArchive,
  '/alerts': Bell,
  '/messages': MessageSquare,
  '/support': LifeBuoy,
  '/admin': Settings,
};

const ADMIN_CHILD_ICONS: Record<string, IconComponent> = {
  '/admin/companies': Building2,
  '/admin/roadmap': Map,
  '/admin/roadmap/my-work': ListChecks,
  '/admin/roadmap/sprints': CalendarRange,
  '/admin/roadmap/requests': Lightbulb,
  '/admin/drawing-vault': FolderArchive,
  '/admin/property-imports': Upload,
  '/admin/subscription-limits': Activity,
  '/admin/widget-permissions': LayoutGrid,
  '/admin/settings': Cog,
  '/reports/reviews': ClipboardCheck,
  '/admin/pricing': DollarSign,
  '/admin/domains': Globe,
  '/admin/subscriptions': CreditCard,
  '/admin/email-templates': Mail,
};

/**
 * Selected-row fill. Section hues replace the old trailing dots — those
 * read as unread badges — and the universal teal pill. Unmapped chrome
 * routes still fall back to `--color-sidebar-active`.
 */
function navActiveStyle(isSelected: boolean, path: string): CSSProperties | undefined {
  if (!isSelected) return undefined;
  return { backgroundColor: sectionActiveColorForPath(path) };
}

const NAV_ITEMS: NavItem[] = SIDEBAR_NAV_ITEMS.map((item) => ({
  ...item,
  icon: TOP_LEVEL_ICONS[item.path] ?? FolderArchive,
  children: item.children?.map((child) => ({
    ...child,
    icon: ADMIN_CHILD_ICONS[child.path] ?? FolderArchive,
  })),
}));

export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const mobileOpen = useUIStore((s) => s.sidebarMobileOpen);
  const setMobileOpen = useUIStore((s) => s.setSidebarMobileOpen);
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const user = useAuthStore((s) => s.user);
  const userRole = user?.role ?? '';
  const logoutMutation = useLogout();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const prefetchOrdersRoute = useCallback(() => {
    prefetchOrderListPageChunk();
    prefetchOrderListData(queryClient);
  }, [queryClient]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (location.pathname.startsWith('/admin')) initial.add('/admin');
    return initial;
  });

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  // PR #49 — hide Dashboard when access-context says the gate is off (BASE-tier
  // clients). SSI Admin/Employee always get dashboardEnabled from the API.
  const hideDashboardNav = useDashboardNavHidden();
  const hideDrawingVaultNav = useDrawingVaultNavHidden();
  const zendeskSupport = isReleaseFeatureEnabled('support-zendesk');
  // DEF-427: Report Review popup must render CM nav (no Admin / Vault Admin / Dashboard).
  const reportReviewCmPerspective =
    isReportReviewSearchParams(searchParams) || isReportReviewSessionActive();
  const reviewBuildingId =
    reportReviewBuildingIdFromSearch(searchParams) ?? reportReviewBuildingIdFromSession();
  const navTo = (path: string) =>
    reportReviewCmPerspective ? reportReviewScopedPath(path, reviewBuildingId) : path;

  const handleSupportZendeskClick = useCallback(async () => {
    const userId = useAuthStore.getState().user?.userId;
    if (typeof userId !== 'number') {
      toast.error('Could not open Support. Please sign in again.');
      return;
    }
    try {
      await openZendeskHelpCenterSso(userId);
    } catch (err) {
      toast.error(
        getApiErrorMessage(err, 'Could not open Support. Please try again.'),
      );
    }
  }, []);

  // DEF-427: child SSI-only gates must honor CM perspective too (Admin parent is
  // already hidden; this keeps Drawing Vault Admin / Companies from leaking if
  // a parent ever reappears in the filtered set).
  const effectiveNavRole = reportReviewCmPerspective ? 'CompanyManager' : userRole;

  const visibleItems = NAV_ITEMS.filter((item) => {
    // Drawing Vault nav follows admin grants (ADR-0019), not TruSpace CBS tier.
    if (item.path === '/drawing-vault' && hideDrawingVaultNav) return false;
    return isSidebarNavItemVisible(item, userRole, {
      isReleaseFeatureEnabled,
      hideDashboardNav,
      reportReviewCmPerspective,
    });
  });

  const toggleExpanded = (path: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const sidebarContent = (isMobile: boolean) => (
    <>
      {/* Logo — on mobile, pad-top by env(safe-area-inset-top) so
          the TruSpace mark sits below the iOS Dynamic Island /
          notch and doesn't get clipped when the drawer slides in.
          On desktop the sidebar is permanent, no notch concerns. */}
      <div
        style={
          isMobile
            ? {
                paddingTop: 'env(safe-area-inset-top)',
                height: 'calc(var(--header-height) + env(safe-area-inset-top))',
              }
            : undefined
        }
        className="flex h-[var(--header-height)] items-center justify-center border-b border-sidebar-hover px-4"
      >
        {/* SVG lockups, not the legacy PNGs: the rail is the app's most-seen
            brand moment and a raster mark at 1x visibly softens on a HiDPI
            screen. Both files carry a white fill, which is correct on the rail
            in BOTH themes (Deep Purple in light, near-black in dark). The
            collapsed symbol is h-7 rather than h-8 because at 296:257 an h-8
            symbol is 37px wide and the collapsed rail only clears 36px. */}
        <div className="flex flex-1 items-center justify-center">
          {(isMobile || !collapsed) ? (
            <img
              src="/TS_White.svg"
              alt="TruSpace"
              className="h-8 w-auto object-contain"
            />
          ) : (
            <img
              src="/TS_Symbol_White.svg"
              alt="TruSpace"
              className="h-7 w-auto object-contain"
            />
          )}
        </div>
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className="rounded p-1.5 text-sidebar-text transition-colors duration-[var(--duration-fast)] hover:text-sidebar-text-active"
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        <ul className="flex flex-col gap-0.5">
          {visibleItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedSections.has(item.path);
            const isInSection = location.pathname.startsWith(item.path) && item.path !== '/dashboard';
            const showExpanded = isMobile || !collapsed;

            if (hasChildren && showExpanded) {
              return (
                <li key={item.path}>
                  {/* An in-section PARENT takes the neutral hover step, not the
                      coloured active pill: the pill marks the one row that is
                      the current route, and that row is the child below. */}
                  <button
                    onClick={() => toggleExpanded(item.path)}
                    className={cn(
                      'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-[var(--duration-fast)]',
                      isInSection
                        ? 'bg-sidebar-hover text-sidebar-text-active'
                        : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
                    )}
                    aria-label={`${item.label} section`}
                    aria-expanded={isExpanded}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform duration-[var(--duration-fast)]',
                        isExpanded && 'rotate-180',
                      )}
                    />
                  </button>
                  {/* `grid-rows-[0fr]` → `[1fr]` animates the children's
                      intrinsic height without measuring it, which is what
                      `motion.mdc` §3 asks for — the previous `max-h-[500px]`
                      capped the list AND made the easing depend on how far
                      short of 500px the real height fell. */}
                  <div
                    className={cn(
                      'grid transition-[grid-template-rows,opacity] duration-[var(--duration-base)] ease-in-out',
                      isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                    )}
                  >
                    <ul className="ml-4 mt-0.5 flex min-h-0 flex-col gap-0.5 overflow-hidden border-l border-sidebar-hover pl-3">
                      {item
                        .children!.filter((child) => {
                          if (child.flag && !isReleaseFeatureEnabled(child.flag)) {
                            return false;
                          }
                          const ssiOnly =
                            child.path === '/admin/companies' ||
                            child.path === '/admin/drawing-vault';
                          if (ssiOnly && !isSSIRole(effectiveNavRole)) {
                            return false;
                          }
                          return true;
                        })
                        .map((child) => {
                          const ChildIcon =
                            ADMIN_CHILD_ICONS[child.path] ?? FolderArchive;
                          return (
                        <li key={child.path}>
                          <NavLink
                            to={navTo(child.path)}
                            end={child.path === '/admin/roadmap'}
                            style={() =>
                              navActiveStyle(
                                isAdminChildNavActive(child.path, location.pathname),
                                child.path,
                              )
                            }
                            className={() => {
                              const isActive = isAdminChildNavActive(
                                child.path,
                                location.pathname,
                              );
                              return cn(
                                'group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors duration-[var(--duration-fast)]',
                                isActive
                                  ? 'text-sidebar-text-active'
                                  : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
                              );
                            }}
                          >
                            <ChildIcon className="h-4 w-4 shrink-0" />
                            <span>{child.label}</span>
                          </NavLink>
                        </li>
                          );
                        })}
                    </ul>
                  </div>
                </li>
              );
            }

            // Zendesk mode: Support is an action (SSO popup), not a route link.
            if (item.path === '/support' && zendeskSupport) {
              return (
                <li key={item.path}>
                  <button
                    type="button"
                    onClick={() => {
                      void handleSupportZendeskClick();
                    }}
                    className={cn(
                      'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-[var(--duration-fast)]',
                      'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
                    )}
                    aria-label="Open Support Help Center"
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {showExpanded && <span>{item.label}</span>}
                  </button>
                </li>
              );
            }

            return (
              <li key={item.path}>
                <NavLink
                  to={hasChildren ? navTo(item.children![0].path) : navTo(item.path)}
                  onMouseEnter={item.path === '/orders' ? prefetchOrdersRoute : undefined}
                  onFocus={item.path === '/orders' ? prefetchOrdersRoute : undefined}
                  aria-label={item.label}
                  style={({ isActive }) => navActiveStyle(isActive || isInSection, item.path)}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-[var(--duration-fast)]',
                      isActive || isInSection
                        ? 'text-sidebar-text-active'
                        : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {showExpanded && <span>{item.label}</span>}
                </NavLink>
                {item.path === '/properties' ? (
                  <SidebarRecentProperties
                    expanded={showExpanded}
                    role={effectiveNavRole}
                    toPath={navTo}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom section: Account + Theme. Notifications bell relocated
          to the top-right of the host shell per PRD §07 §4.1 — see
          Header.tsx. On mobile, pad the bottom with the iOS home-
          indicator inset so the Sign out button doesn't sit on top
          of the system gesture bar. */}
      <div
        style={isMobile ? { paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' } : undefined}
        className="border-t border-sidebar-hover px-3 py-3"
      >
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-text transition-colors duration-[var(--duration-fast)] hover:bg-sidebar-hover hover:text-sidebar-text-active"
        >
          {theme === 'light' ? <Moon className="h-4 w-4 shrink-0" /> : <Sun className="h-4 w-4 shrink-0" />}
          {(isMobile || !collapsed) && <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
        </button>

        {/* User account — PRD §10 §4.1 "click the avatar → My Account".
            FR-265: the collapsed rail is 68px. Footer `px-3` plus this card's
            `px-3` left ~20px for a 32px circle, so the avatar sat off-center.
            Drop the card pad and center the circle when collapsed — same
            pattern as the logo mark above. */}
        <div
          className={cn(
            'mt-1 rounded-lg bg-sidebar-hover py-2.5',
            collapsed && !isMobile ? 'flex justify-center px-0' : 'px-3',
          )}
        >
          <button
            type="button"
            onClick={() => navigate('/account')}
            aria-label="Open My Account"
            className={cn(
              'flex items-center gap-3 rounded-md text-left transition-colors duration-[var(--duration-fast)] hover:bg-sidebar-bg',
              collapsed && !isMobile ? 'justify-center' : 'w-full',
            )}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-bg text-sidebar-accent">
              <User className="h-4 w-4" />
            </div>
            {(isMobile || !collapsed) && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-sidebar-text-active">{user?.firstName} {user?.lastName}</p>
                <p className="truncate text-xs text-sidebar-text">{user?.email}</p>
              </div>
            )}
          </button>
          {/* Sign out is routine, not destructive, and the rail has no status
              palette — the retired `text-red-400` stopgap had no readable
              equivalent against Deep Purple. The icon plus the word carry the
              meaning, per `a11y.mdc` §5. */}
          {(isMobile || !collapsed) && (
            <button
              onClick={() => logoutMutation.mutate()}
              className="mt-2 flex w-full items-center gap-2 rounded-md px-1 py-1 text-xs text-sidebar-text transition-colors duration-[var(--duration-fast)] hover:text-sidebar-text-active"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          )}
        </div>
      </div>

      {/* Collapse toggle (desktop only) */}
      {!isMobile && (
        <button
          onClick={toggleSidebar}
          className="flex h-11 items-center justify-center border-t border-sidebar-hover text-sidebar-text transition-colors duration-[var(--duration-fast)] hover:text-sidebar-accent"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      )}
    </>
  );

  return (
    <>
      {/* Desktop sidebar — sits below the impersonation banner when active. */}
      <aside
        style={{
          top: 'var(--impersonation-banner-height)',
          height: 'calc(100dvh - var(--impersonation-banner-height))',
        }}
        className={cn(
          // `transition-[width]`, not `transition-all`. Width is a
          // layout-triggering property that `motion.mdc` §4 would rather we
          // animate as a transform, but the rail is in flow — the page's
          // content margin tracks this width — so a transform would slide the
          // rail out from under the content instead of resizing it. Naming the
          // one property at least keeps the transition off colour and shadow.
          'fixed left-0 z-sidebar hidden flex-col border-r border-sidebar-hover bg-sidebar-bg shadow-xl transition-[width] duration-[var(--duration-base)] ease-[var(--ease-out)] lg:flex',
          collapsed ? 'w-[var(--sidebar-collapsed-width)]' : 'w-[var(--sidebar-width)]',
        )}
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-sidebar lg:hidden">
          {/* Backdrop. A real button so the dismiss gesture is reachable from
              the keyboard; no `backdrop-blur`, which `motion.mdc` §4 rules out
              on the low-end integrated GPUs this app targets. */}
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-black/80"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <aside className="absolute left-0 top-0 flex h-full w-70 flex-col bg-sidebar-bg shadow-lg animate-in slide-in-from-left duration-[var(--duration-base)]">
            {sidebarContent(true)}
          </aside>
        </div>
      )}
    </>
  );
}
